import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { PdfUpload } from "@/components/PdfUpload";
import { FileLink } from "@/components/FileLink";
import { toast } from "sonner";
import { Users, ClipboardList, Clock, FileText, Loader2, Plus, Download, Check, X, ShieldPlus, UserCog, UserMinus, UserX, UserCheck, Trash2, Pencil, CheckCircle2, Ban, Archive, ArchiveRestore, MessageSquare, Clapperboard, Tags, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CaseStudyTab from "./admin/CaseStudyTab";
import ExpertiseTab from "./admin/ExpertiseTab";
import LeadsTab from "./admin/LeadsTab";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CreativePicker } from "@/components/CreativePicker";
import { DeletedFilter, DeletedRowActions, DeletedMeta, RecordSummary } from "@/components/DeletedControls";

const TABS = [
  { id: "users", label: "Users & Onboarding", icon: Users },
  { id: "leads", label: "Creatives", icon: UserPlus },
  { id: "jobs", label: "Jobs", icon: ClipboardList },
  { id: "timesheets", label: "Timesheets", icon: Clock },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "content", label: "Case Study", icon: Clapperboard },
  { id: "expertise", label: "Areas of Expertise", icon: Tags },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  };
  useEffect(() => { loadStats(); }, [tab]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar title="Admin Dashboard" />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-bebas text-5xl mb-8">CONTROL CENTER</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-10" data-testid="admin-stats">
            {[
              { k: "total_users", l: "Total Users" },
              { k: "pending_onboarding", l: "Awaiting Approval" },
              { k: "active_contractors", l: "Active Creatives" },
              { k: "open_jobs", l: "Open Jobs" },
              { k: "pending_timesheets", l: "Timesheets" },
              { k: "pending_invoices", l: "Pending Inv." },
              { k: "new_inquiries", l: "New Inquiries" },
            ].map((s) => (
              <div key={s.k} className="bg-[#111111] p-5">
                <div className="font-bebas text-4xl text-[#FF4D00]">{stats[s.k]}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap border-b border-[#2A2A2A] mb-8">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#FF4D00] text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && <UsersTab refreshStats={loadStats} />}
        {tab === "leads" && <LeadsTab refreshStats={loadStats} />}
        {tab === "jobs" && <JobsTab />}
        {tab === "timesheets" && <TimesheetsTab refreshStats={loadStats} />}
        {tab === "invoices" && <InvoicesTab refreshStats={loadStats} />}
        {tab === "inquiries" && <InquiriesTab refreshStats={loadStats} />}
        {tab === "content" && <CaseStudyTab />}
        {tab === "expertise" && <ExpertiseTab />}
      </main>
    </div>
  );
}

const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";

function Loader() { return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#FF4D00]" /></div>; }
function Empty({ text }) { return <div className="border border-dashed border-[#2A2A2A] p-12 text-center text-sm text-[#A1A1AA]">{text}</div>; }

function InquiriesTab({ refreshStats }) {
  const [items, setItems] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/inquiries"); setItems(data.inquiries); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    try { await api.post(`/admin/inquiries/${id}/status`, fd); toast.success("Inquiry updated"); load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (q) => {
    try { await api.delete(`/admin/inquiries/${q.id}`); toast.success("Inquiry deleted"); setConfirmDel(null); load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!items) return <Loader />;
  if (!items.length) return <Empty text="No inquiries yet." />;
  return (
    <div className="border border-[#2A2A2A] overflow-x-auto" data-testid="admin-inquiries-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Project</th><th className="p-4">Budget</th><th className="p-4">Brief</th><th className="p-4">Received</th><th className="p-4">Status</th><th className="p-4">Update</th><th className="p-4">Actions</th>
        </tr></thead>
        <tbody>
          {items.map((q) => (
            <tr key={q.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors align-top" data-testid={`inquiry-row-${q.id}`}>
              <td className="p-4 text-white whitespace-nowrap">{q.name}</td>
              <td className="p-4 text-[#A1A1AA] whitespace-nowrap">{q.email}</td>
              <td className="p-4 text-[#A1A1AA]" style={{ maxWidth: "26rem" }}>{q.project_details}</td>
              <td className="p-4 text-[#A1A1AA] whitespace-nowrap">{q.budget || "—"}</td>
              <td className="p-4">{q.brief_file_id ? <FileLink fileId={q.brief_file_id} testId={`brief-view-${q.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> Brief</FileLink> : <span className="text-xs text-[#555]">—</span>}</td>
              <td className="p-4 text-[#A1A1AA] whitespace-nowrap">{new Date(q.created_at).toLocaleDateString()}</td>
              <td className="p-4"><StatusBadge status={q.status} /></td>
              <td className="p-4">
                <select data-testid={`inquiry-status-${q.id}`} className="bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF4D00]" value={q.status} onChange={(e) => setStatus(q.id, e.target.value)}>
                  <option value="new">New</option><option value="followed_up">Followed Up</option><option value="archived">Archived</option>
                </select>
              </td>
              <td className="p-4"><button data-testid={`inquiry-delete-${q.id}`} onClick={() => setConfirmDel(q)} title="Delete inquiry" className="border border-[#EF4444] text-[#EF4444] px-2 py-1 hover:bg-[#EF444420] transition-colors"><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {confirmDel && (
        <ConfirmDialog
          title="Delete Inquiry"
          message={`Permanently delete the inquiry from ${confirmDel.name}? This removes it from your dashboard and cannot be undone.`}
          confirmLabel="Delete Inquiry"
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => del(confirmDel)}
        />
      )}
    </div>
  );
}

function UsersTab({ refreshStats }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [invite, setInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [reject, setReject] = useState(null); // {id, name}
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users);
  };
  useEffect(() => { load(); }, []);

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doInvite = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    try {
      await api.post("/admin/invite", form);
      toast.success("Admin invited");
      setInvite(false); setForm({ name: "", email: "", password: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doReject = async () => {
    setRejecting(true);
    try {
      await api.post(`/admin/users/${reject.id}/reject`, { reason });
      toast.success("Onboarding rejected — email sent");
      setReject(null); setReason(""); await load(); refreshStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setRejecting(false); }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`Permanently remove ${u.name}? They will be deleted from the platform and can no longer sign in.`)) return;
    action(() => api.delete(`/admin/users/${u.id}`), "User deleted");
  };

  if (!users) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button data-testid="invite-admin-btn" onClick={() => setInvite(!invite)} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-sm hover:bg-[#E64500] transition-colors"><ShieldPlus className="w-4 h-4" /> Invite admin</button>
      </div>
      {invite && (
        <div className="border border-[#2A2A2A] bg-[#111111] p-6 mb-6 grid sm:grid-cols-3 gap-4" data-testid="invite-form">
          <input data-testid="invite-name-input" className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="invite-email-input" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="invite-password-input" className={inputCls} placeholder="Temp password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <button data-testid="invite-submit-btn" onClick={doInvite} className="bg-[#FF4D00] text-white px-4 py-3 text-sm hover:bg-[#E64500] transition-colors sm:col-span-3">Create admin account</button>
        </div>
      )}

      <div className="border border-[#2A2A2A] overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Status</th><th className="p-4">Documents</th><th className="p-4">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="p-4 text-white">{u.name}</td>
                <td className="p-4 text-[#A1A1AA]">{u.email}</td>
                <td className="p-4">
                  <span className="text-xs uppercase tracking-[0.1em] text-[#FF4D00]">{u.role}</span>
                  {u.active === false && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-[#EF4444] border border-[#EF4444] px-1.5 py-0.5" data-testid={`deactivated-tag-${u.id}`}>Deactivated</span>}
                </td>
                <td className="p-4"><StatusBadge status={u.status} /></td>
                <td className="p-4">
                  {u.onboarding ? (
                    <div className="space-y-2">
                      <div className="flex gap-3">
                        <FileLink fileId={u.onboarding.w9_file_id} testId={`w9-view-${u.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> W-9</FileLink>
                        <FileLink fileId={u.onboarding.nda_file_id} testId={`nda-view-${u.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> NDA</FileLink>
                      </div>
                      {((u.onboarding.expertise?.length || 0) > 0 || u.onboarding.expertise_other) && (
                        <div className="flex flex-wrap gap-1 max-w-xs" data-testid={`user-expertise-${u.id}`}>
                          {(u.onboarding.expertise || []).map((x) => <span key={x} className="text-[10px] uppercase tracking-wide border border-[#2A2A2A] text-[#A1A1AA] px-1.5 py-0.5">{x === "Other" && u.onboarding.expertise_other ? u.onboarding.expertise_other : x}</span>)}
                        </div>
                      )}
                    </div>
                  ) : <span className="text-xs text-[#555]">—</span>}
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    {u.status === "onboarding_submitted" && (
                      <>
                        <button data-testid={`approve-btn-${u.id}`} onClick={() => action(() => api.post(`/admin/users/${u.id}/approve`), "Contractor approved — welcome email sent")} className="border border-[#10B981] text-[#10B981] px-2 py-1 hover:bg-[#10B98120] transition-colors" title="Approve"><Check className="w-4 h-4" /></button>
                        <button data-testid={`reject-btn-${u.id}`} onClick={() => { setReject({ id: u.id, name: u.name }); setReason(""); }} className="border border-[#EF4444] text-[#EF4444] px-2 py-1 hover:bg-[#EF444420] transition-colors" title="Reject"><X className="w-4 h-4" /></button>
                      </>
                    )}
                    {u.role !== "admin" && (
                      <button data-testid={`promote-btn-${u.id}`} onClick={() => action(() => api.post(`/admin/users/${u.id}/promote`), "Promoted to admin")} className="border border-[#2A2A2A] text-white px-2 py-1 hover:bg-[#1A1A1A] transition-colors" title="Promote to admin"><UserCog className="w-4 h-4" /></button>
                    )}
                    {u.role === "admin" && u.id !== me?.id && (
                      <button data-testid={`demote-btn-${u.id}`} onClick={() => action(() => api.post(`/admin/users/${u.id}/demote`), "Demoted to contractor")} className="border border-[#2A2A2A] text-[#A1A1AA] px-2 py-1 hover:bg-[#1A1A1A] hover:text-white transition-colors" title="Demote to contractor"><UserMinus className="w-4 h-4" /></button>
                    )}
                    {u.id !== me?.id && (u.active === false ? (
                      <button data-testid={`activate-btn-${u.id}`} onClick={() => action(() => api.post(`/admin/users/${u.id}/activate`), "User reactivated")} className="border border-[#10B981] text-[#10B981] px-2 py-1 hover:bg-[#10B98120] transition-colors" title="Reactivate user"><UserCheck className="w-4 h-4" /></button>
                    ) : (
                      <button data-testid={`deactivate-btn-${u.id}`} onClick={() => action(() => api.post(`/admin/users/${u.id}/deactivate`), "User deactivated")} className="border border-[#F59E0B] text-[#F59E0B] px-2 py-1 hover:bg-[#F59E0B20] transition-colors" title="Deactivate user"><UserX className="w-4 h-4" /></button>
                    ))}
                    {u.id !== me?.id && (
                      <button data-testid={`delete-btn-${u.id}`} onClick={() => doDelete(u)} className="border border-[#EF4444] text-[#EF4444] px-2 py-1 hover:bg-[#EF444420] transition-colors" title="Delete user"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reject && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="w-full max-w-md border border-[#2A2A2A] bg-[#111111]">
            <div className="h-1 bg-[#FF4D00]" />
            <div className="p-7">
              <h3 className="font-bebas text-2xl tracking-wide mb-1">REJECT ONBOARDING</h3>
              <p className="text-sm text-[#A1A1AA] mb-5">Provide feedback for <span className="text-white">{reject.name}</span>. This reason is included in the rejection email.</p>
              <textarea
                data-testid="reject-reason-input"
                className={`${inputCls} min-h-[110px]`}
                placeholder="Reason for rejection (e.g. W-9 was illegible, NDA not signed)…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
              <div className="flex justify-end gap-3 mt-5">
                <button data-testid="reject-cancel-btn" onClick={() => { setReject(null); setReason(""); }} className="border border-[#2A2A2A] px-5 py-2.5 text-sm hover:bg-[#1A1A1A] transition-colors">Cancel</button>
                <button data-testid="reject-confirm-btn" onClick={doReject} disabled={rejecting} className="bg-[#EF4444] text-white px-5 py-2.5 text-sm hover:bg-[#dc2626] transition-colors flex items-center gap-2 disabled:opacity-60">
                  {rejecting && <Loader2 className="w-4 h-4 animate-spin" />} Reject & notify
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobsTab() {
  const [jobs, setJobs] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", job_number: "", scope: "", due_date: "", assigned_contractor_ids: [], status: "" });
  const [assets, setAssets] = useState([]);
  const [existingAssets, setExistingAssets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [j, c] = await Promise.all([api.get("/admin/jobs"), api.get("/admin/contractors")]);
    setJobs(j.data.jobs); setContractors(c.data.contractors);
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: "", job_number: "", scope: "", due_date: "", assigned_contractor_ids: [], status: "" }); setAssets([]); setExistingAssets([]); setEditingId(null); };
  const openForm = (job) => {
    if (job) {
      setEditingId(job.id);
      setForm({ title: job.title || "", job_number: job.job_number || "", scope: job.scope || "", due_date: job.due_date || "", assigned_contractor_ids: job.assigned_contractor_ids || (job.assigned_contractor_id ? [job.assigned_contractor_id] : []), status: job.status || "" });
      setExistingAssets(job.assets || []);
      setAssets([]);
    } else { resetForm(); }
    setOpen(true);
  };
  const cancelForm = () => { setOpen(false); resetForm(); };

  const save = async () => {
    if (!form.title || !form.scope || !form.due_date) { toast.error("Fill title, scope and due date"); return; }
    setSaving(true);
    const payload = { ...form, assigned_contractor_ids: form.assigned_contractor_ids, asset_file_ids: [...existingAssets.map((a) => a.id), ...assets.map((a) => a.id)] };
    try {
      if (editingId) { await api.put(`/admin/jobs/${editingId}`, payload); toast.success("Job updated"); }
      else { await api.post("/admin/jobs", payload); toast.success("Job created"); }
      setOpen(false); resetForm(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setJobStatus = async (jobId, status, msg) => {
    const fd = new FormData(); fd.append("status", status);
    try { await api.post(`/admin/jobs/${jobId}/status`, fd); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const assign = async (jobId, contractorId) => {
    if (!contractorId) return;
    const fd = new FormData(); fd.append("contractor_id", contractorId);
    try { await api.post(`/admin/jobs/${jobId}/assign`, fd); toast.success("Creative assigned"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const unassign = async (jobId, contractorId) => {
    const fd = new FormData(); fd.append("contractor_id", contractorId);
    try { await api.post(`/admin/jobs/${jobId}/unassign`, fd); toast.success("Creative removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const archiveJob = async (jobId, archived, msg) => {
    try { await api.post(`/admin/jobs/${jobId}/${archived ? "archive" : "unarchive"}`); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteJob = async (j) => {
    const ts = j.timesheet_count || 0;
    const inv = j.invoice_count || 0;
    const extras = (ts || inv) ? ` This will also delete ${ts} timesheet(s) and ${inv} invoice(s).` : "";
    if (!window.confirm(`Permanently delete "${j.title}"?${extras} This cannot be undone.`)) return;
    try { await api.delete(`/admin/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const jobCard = (j) => (
    <div key={j.id} className="bg-[#111111] p-6">
      <div className="flex items-start justify-between mb-2">
        <div>
          {j.job_number && <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D00] mb-1">Job #{j.job_number}</div>}
          <h3 className="font-bebas text-2xl tracking-wide">{j.title}</h3>
        </div>
        <StatusBadge status={j.status} />
      </div>
      <p className="text-sm text-[#A1A1AA] mb-4">{j.scope}</p>
      <div className="text-xs text-[#A1A1AA] mb-4">Due {j.due_date} · {j.assets?.length || 0} asset(s)</div>
      {(j.assigned_names?.length || 0) > 0 ? (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`assigned-chips-${j.id}`}>
          {j.assigned_names.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-xs bg-[#FF4D0020] border border-[#FF4D00] text-[#FF4D00] px-2 py-1">
              {c.name}
              <button data-testid={`unassign-${j.id}-${c.id}`} onClick={() => unassign(j.id, c.id)} title="Remove creative" className="hover:text-white"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      ) : null}
      {(() => {
        const assignedIds = (j.assigned_names || []).map((c) => c.id);
        const avail = contractors.filter((c) => !assignedIds.includes(c.id));
        if (!avail.length) return assignedIds.length ? null : <span className="text-xs text-[#555]">No creatives with login accounts yet</span>;
        return (
          <select data-testid={`assign-select-${j.id}`} className={inputCls} value="" onChange={(e) => e.target.value && assign(j.id, e.target.value)}>
            <option value="">{assignedIds.length ? "Add another creative…" : "Assign creative…"}</option>
            {avail.map((c) => <option key={c.id} value={c.id}>{c.name}{c.role === "admin" ? " (Admin)" : ""}</option>)}
          </select>
        );
      })()}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#2A2A2A]">
        {j.archived ? (
          <>
            <button data-testid={`unarchive-job-${j.id}`} onClick={() => archiveJob(j.id, false, "Job restored")} className="inline-flex items-center gap-1 border border-[#FF4D00] text-[#FF4D00] px-3 py-1.5 text-xs hover:bg-[#FF4D0020] transition-colors"><ArchiveRestore className="w-3 h-3" /> Restore</button>
            <button data-testid={`delete-job-${j.id}`} onClick={() => deleteJob(j)} className="inline-flex items-center gap-1 border border-[#EF4444] text-[#EF4444] px-3 py-1.5 text-xs hover:bg-[#EF444420] transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
          </>
        ) : (
          <>
            <button data-testid={`edit-job-${j.id}`} onClick={() => openForm(j)} className="inline-flex items-center gap-1 border border-[#2A2A2A] text-white px-3 py-1.5 text-xs hover:bg-[#1A1A1A] transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
            {j.status !== "completed" && j.status !== "cancelled" && (
              <>
                <button data-testid={`complete-job-${j.id}`} onClick={() => setJobStatus(j.id, "completed", "Job marked complete")} className="inline-flex items-center gap-1 border border-[#10B981] text-[#10B981] px-3 py-1.5 text-xs hover:bg-[#10B98120] transition-colors"><CheckCircle2 className="w-3 h-3" /> Complete</button>
                <button data-testid={`cancel-job-${j.id}`} onClick={() => setJobStatus(j.id, "cancelled", "Job cancelled")} className="inline-flex items-center gap-1 border border-[#EF4444] text-[#EF4444] px-3 py-1.5 text-xs hover:bg-[#EF444420] transition-colors"><Ban className="w-3 h-3" /> Cancel Job</button>
              </>
            )}
            <button data-testid={`archive-job-${j.id}`} onClick={() => archiveJob(j.id, true, "Job archived")} className="inline-flex items-center gap-1 border border-[#2A2A2A] text-[#A1A1AA] px-3 py-1.5 text-xs hover:bg-[#1A1A1A] hover:text-white transition-colors"><Archive className="w-3 h-3" /> Archive</button>
            <button data-testid={`delete-job-${j.id}`} onClick={() => deleteJob(j)} className="inline-flex items-center gap-1 border border-[#EF4444] text-[#EF4444] px-3 py-1.5 text-xs hover:bg-[#EF444420] transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
          </>
        )}
      </div>
    </div>
  );

  if (!jobs) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button data-testid="new-job-btn" onClick={() => (open ? cancelForm() : openForm(null))} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-sm hover:bg-[#E64500] transition-colors"><Plus className="w-4 h-4" /> Create job</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="job-modal" onClick={cancelForm}>
          <div className="w-full max-w-2xl my-10 border border-[#2A2A2A] bg-[#111111]" onClick={(e) => e.stopPropagation()}>
            <div className="h-1 bg-[#FF4D00]" />
            <div className="p-6 space-y-4" data-testid="job-form">
              <h3 className="font-bebas text-2xl tracking-wide">{editingId ? "EDIT JOB" : "CREATE JOB"}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="job-title-input" className={inputCls} placeholder="Job title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input data-testid="job-number-input" className={inputCls} placeholder="Job number (e.g. SCH-1024)" value={form.job_number} onChange={(e) => setForm({ ...form, job_number: e.target.value })} />
          </div>
          <textarea data-testid="job-scope-input" className={`${inputCls} min-h-[100px]`} placeholder="Scope of work" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Due date</label>
              <input data-testid="job-due-input" type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Assign creatives</label>
              <CreativePicker contractors={contractors} selectedIds={form.assigned_contractor_ids} onChange={(ids) => setForm({ ...form, assigned_contractor_ids: ids })} />
            </div>
          </div>
          {editingId && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Job status</label>
              <select data-testid="job-status-select" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="open">Open</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          <PdfUpload testId="job-asset-upload" category="asset" value={assets[0]} onUploaded={(d) => setAssets([...assets, d])} label={`Job asset PDF${assets.length ? ` (${assets.length} new)` : ""}${existingAssets.length ? ` · ${existingAssets.length} existing` : (assets.length ? "" : " (optional)")}`} />
          <div className="flex gap-3">
            <button data-testid="job-submit-btn" onClick={save} disabled={saving} className="bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingId ? "Save changes" : "Create job"}</button>
            <button data-testid="job-cancel-btn" onClick={cancelForm} disabled={saving} className="border border-[#2A2A2A] text-white px-6 py-3 text-sm hover:bg-[#1A1A1A] transition-colors">Cancel</button>
          </div>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const activeJobs = jobs.filter((j) => !j.archived);
        const archivedJobs = jobs.filter((j) => j.archived);
        return (
          <>
            {!activeJobs.length ? <Empty text="No active jobs." /> : (
              <div className="grid md:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="admin-jobs-list">
                {activeJobs.map((j) => jobCard(j))}
              </div>
            )}
            {archivedJobs.length > 0 && (
              <div className="mt-12" data-testid="archived-jobs-section">
                <h3 className="font-bebas text-2xl tracking-wide text-[#A1A1AA] mb-4 flex items-center gap-2"><Archive className="w-4 h-4" /> ARCHIVED ({archivedJobs.length})</h3>
                <div className="grid md:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A] opacity-70">
                  {archivedJobs.map((j) => jobCard(j))}
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

function useDeletable(kind, listKey, refreshStats) {
  const [items, setItems] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/admin/${kind}`, { params: { deleted: showDeleted } });
    setItems(data[listKey]); setDeletedCount(data.deleted_count || 0);
  };
  useEffect(() => { setItems(null); load(); }, [showDeleted]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); setConfirm(null); load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return {
    items, load, showDeleted, setShowDeleted, deletedCount, confirm, setConfirm,
    softDelete: (it) => act(() => api.delete(`/admin/${kind}/${it.id}`), "Moved to Deleted — you can restore it from the Deleted view"),
    restore: (it) => act(() => api.post(`/admin/${kind}/${it.id}/restore`), "Restored"),
    purge: (it) => act(() => api.delete(`/admin/${kind}/${it.id}/permanent`), "Permanently deleted"),
  };
}

function DeleteConfirm({ d, noun, rows, warning, hasPdf }) {
  if (!d.confirm) return null;
  const { mode, item } = d.confirm;
  const purge = mode === "purge";
  return (
    <ConfirmDialog
      title={purge ? `Permanently delete ${noun}?` : `Delete ${noun}?`}
      confirmLabel={purge ? "Delete permanently" : "Delete"}
      message={<RecordSummary rows={rows(item)} warning={purge ? `This removes the ${noun}${hasPdf ? " and makes its PDF permanently unreachable" : ""}. This can't be undone.` : warning?.(item)} />}
      onConfirm={() => (purge ? d.purge(item) : d.softDelete(item))}
      onCancel={() => d.setConfirm(null)}
    />
  );
}

const tsRows = (t) => [["Creative", t.contractor_name], ["Job", t.job_title || "—"], ["Hours", `${t.hours}h`], ["Status", t.status]];
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const invRows = (v) => [["Creative", v.contractor_name], ["Job", v.job_title || "General"], ["Amount", money(v.amount)], ["Status", v.status]];
const invWarning = (v) => (["Approved", "Paid"].includes(v.status) ? "The Invoice Approved email (with the PDF) has already been sent to billing and cannot be recalled. Deleting only removes it from the app." : null);

function TimesheetsTab({ refreshStats }) {
  const d = useDeletable("timesheets", "timesheets", refreshStats);
  const ts = d.items;

  const setStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    try { await api.post(`/admin/timesheets/${id}/status`, fd); toast.success(`Timesheet ${status}`); d.load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <DeletedFilter showDeleted={d.showDeleted} setShowDeleted={d.setShowDeleted} deletedCount={d.deletedCount} testPrefix="ts" />
      {!ts ? <Loader /> : !ts.length ? <Empty text={d.showDeleted ? "No deleted timesheets." : "No timesheets submitted yet."} /> : (
    <div className="border border-[#2A2A2A] overflow-x-auto" data-testid="admin-timesheets-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          <th className="p-4">Creative</th><th className="p-4">Project</th><th className="p-4">Start</th><th className="p-4">End</th><th className="p-4">Hours</th><th className="p-4">Status</th><th className="p-4">Actions</th>
        </tr></thead>
        <tbody>
          {ts.map((t) => (
            <tr key={t.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors">
              <td className="p-4 text-white">{t.contractor_name}{d.showDeleted && <DeletedMeta item={t} />}</td>
              <td className="p-4 text-[#A1A1AA]">{t.job_title}</td>
              <td className="p-4 text-[#A1A1AA]">{t.start_time.replace("T", " ")}</td>
              <td className="p-4 text-[#A1A1AA]">{t.end_time.replace("T", " ")}</td>
              <td className="p-4 text-white">{t.hours}h</td>
              <td className="p-4"><StatusBadge status={t.status} /></td>
              <td className="p-4">
                {d.showDeleted ? <DeletedRowActions item={t} testPrefix="ts" onRestore={d.restore} onPurge={(it) => d.setConfirm({ mode: "purge", item: it })} /> : (
                  <div className="flex gap-2">
                    {t.status === "submitted" && (<>
                      <button data-testid={`ts-approve-${t.id}`} onClick={() => setStatus(t.id, "approved")} className="border border-[#10B981] text-[#10B981] px-2 py-1 hover:bg-[#10B98120] transition-colors"><Check className="w-4 h-4" /></button>
                      <button data-testid={`ts-reject-${t.id}`} onClick={() => setStatus(t.id, "rejected")} className="border border-[#EF4444] text-[#EF4444] px-2 py-1 hover:bg-[#EF444420] transition-colors"><X className="w-4 h-4" /></button>
                    </>)}
                    <button data-testid={`ts-delete-${t.id}`} onClick={() => d.setConfirm({ mode: "delete", item: t })} title="Delete timesheet" className="border border-[#2A2A2A] text-[#A1A1AA] px-2 py-1 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
      <DeleteConfirm d={d} noun="timesheet" rows={tsRows} />
    </div>
  );
}

function InvoicesTab({ refreshStats }) {
  const d = useDeletable("invoices", "invoices", refreshStats);
  const inv = d.items;

  const setStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    try { await api.post(`/admin/invoices/${id}/status`, fd); toast.success(`Invoice marked ${status}`); d.load(); refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <DeletedFilter showDeleted={d.showDeleted} setShowDeleted={d.setShowDeleted} deletedCount={d.deletedCount} testPrefix="inv" />
      {!inv ? <Loader /> : !inv.length ? <Empty text={d.showDeleted ? "No deleted invoices." : "No invoices uploaded yet."} /> : (
    <div className="border border-[#2A2A2A] overflow-x-auto" data-testid="admin-invoices-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          <th className="p-4">Creative</th><th className="p-4">Project</th><th className="p-4">Amount</th><th className="p-4">PDF</th><th className="p-4">Status</th><th className="p-4">{d.showDeleted ? "Actions" : "Update"}</th>
        </tr></thead>
        <tbody>
          {inv.map((v) => (
            <tr key={v.id} className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors">
              <td className="p-4 text-white">{v.contractor_name}{d.showDeleted && <DeletedMeta item={v} />}</td>
              <td className="p-4 text-[#A1A1AA]">{v.job_title || "General"}</td>
              <td className="p-4 text-white">{money(v.amount)}</td>
              <td className="p-4">{d.showDeleted ? <span className="text-xs text-[#555]">Hidden</span> : <FileLink fileId={v.invoice_file_id} testId={`admin-invoice-view-${v.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1"><Download className="w-4 h-4" /> View</FileLink>}</td>
              <td className="p-4"><StatusBadge status={v.status} /></td>
              <td className="p-4">
                {d.showDeleted ? <DeletedRowActions item={v} testPrefix="inv" onRestore={d.restore} onPurge={(it) => d.setConfirm({ mode: "purge", item: it })} /> : (
                  <div className="flex items-center gap-2">
                    <select data-testid={`invoice-status-${v.id}`} className="bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF4D00]" value={v.status} onChange={(e) => setStatus(v.id, e.target.value)}>
                      <option>Pending</option><option>Approved</option><option>Paid</option>
                    </select>
                    <button data-testid={`inv-delete-${v.id}`} onClick={() => d.setConfirm({ mode: "delete", item: v })} title="Delete invoice" className="border border-[#2A2A2A] text-[#A1A1AA] px-2 py-1.5 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
      <DeleteConfirm d={d} noun="invoice" rows={invRows} warning={invWarning} hasPdf />
    </div>
  );
}
